import type {
  CatalogModel,
  CatalogResponse,
  UsageByKeyEntry,
  UsageRequestLogEntry,
} from '@nadoutong/contracts';
import { ArrowLeftRight, Braces, Landmark, RefreshCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCatalog } from '../api/useCatalog.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useUsageByKey, useUsageRequests, useUsageSummary } from '../api/useUsage.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import {
  formatCnyRateFromUsdRate,
  formatTokens,
  formatUsdMicrosAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  unitAwareTokenCell,
} from '../components/formatters.ts';
import { apiKeyDisplayName } from '../components/apiKeyName.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey, TParams } from '../i18n/index.ts';
import { dayRangeToIsoWindow, rollingDayRange, todayLocalDay } from './usage/format.ts';

/**
 * `buildRecommendations`, `displayKey` and `formatContextWindow` produce
 * user-visible copy but live at module scope, where hooks are
 * unavailable, so they take the translator their caller already holds.
 * `buildContextTemplate` stays untranslated: its output is a code
 * artifact the user copies into an ignore file.
 */
type Translate = (key: I18nKey, params?: TParams) => string;

type CacheSummary = {
  readonly successRequests: number;
  readonly cacheHitRequests: number;
  readonly cacheEligibleInputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  readonly requestCacheHitRate: number | null;
  readonly tokenCacheHitRate: number | null;
  readonly cacheReadWriteRatio: number | null;
};

type ModelOption = {
  readonly key: string;
  readonly model: CatalogModel;
  readonly displayName: string;
  readonly inputRateUsd: number;
  readonly outputRateUsd: number;
  readonly cacheReadRateUsd: number | null;
  readonly cacheWriteRateUsd: number | null;
  readonly effectiveMultiplier: number;
};

const TEMPLATE_GROUPS = [
  {
    id: 'node',
    lines: ['node_modules/', 'dist/', '.vite/', '.turbo/', '*.tsbuildinfo'],
  },
  {
    id: 'python',
    label: 'Python',
    lines: ['__pycache__/', '*.pyc', '.venv/', '.pytest_cache/', '.mypy_cache/'],
  },
  {
    id: 'rust',
    label: 'Rust',
    lines: ['target/', '**/*.rs.bk'],
  },
] as const;

function templateGroupLabel(
  group: (typeof TEMPLATE_GROUPS)[number],
  t: ReturnType<typeof useT>,
): string {
  return 'label' in group ? group.label : t('optimization.template.group.node');
}

export function Optimization(): JSX.Element {
  const t = useT();
  // The same 30-day calendar window `/usage` and `/requests` build, not
  // a private sliding one: this page's spend is read side by side with
  // the `/usage` 30d figure, and a `now - 30*24h` window both
  // disagreed with it and made these numbers drift on every refresh
  // within one calendar day.
  const range = useMemo(() => dayRangeToIsoWindow(rollingDayRange(todayLocalDay(), 30)), []);
  const summaryQ = useUsageSummary(range);
  const byKeyQ = useUsageByKey(range);
  const requestsQ = useUsageRequests({ ...range, limit: 50, offset: 0 });
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const cacheSummary = useMemo(() => summarizeCache(byKeyQ.data?.keys ?? []), [byKeyQ.data?.keys]);
  const modelOptions = useMemo(() => buildModelOptions(catalogQ.data), [catalogQ.data]);
  const bestCacheModel =
    modelOptions.find((option) => option.model.supports_prompt_cache) ?? modelOptions[0] ?? null;
  const requestHotspots = useMemo(
    () => (requestsQ.data?.rows ?? []).slice().sort(compareRequestsBySpend).slice(0, 4),
    [requestsQ.data?.rows],
  );
  const summary = summaryQ.data;

  return (
    <section data-testid="optimization-page" className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <h2 className="font-display text-display text-ink">{t('optimization.title')}</h2>
          <p className="text-body leading-6 text-ink-low">{t('optimization.subtitle')}</p>
        </div>
        <div className="rounded-lg border border-line bg-card px-4 py-3 font-mono text-caption text-ink-low">
          {t('optimization.window')}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <OptimizationMetric
          label={t('optimization.metric.spend')}
          value={
            summary !== undefined
              ? formatUsdMicrosOrCentsAsCnyDisplay(
                  summary.totalCostUsdMicros,
                  summary.totalCostUsdCents,
                  cnyPerUsd,
                )
              : '-'
          }
          detail={
            summary !== undefined
              ? t('usage.requests_count', { count: formatTokens(summary.totalRequests) })
              : t('optimization.metric.loading')
          }
          icon={Landmark}
          testId="optimization-summary-spend"
        />
        <OptimizationMetric
          label={t('usage.summary.cache_hit')}
          value={formatPercent(cacheSummary.requestCacheHitRate)}
          detail={t('optimization.metric.token_hit', {
            rate: formatPercent(cacheSummary.tokenCacheHitRate),
          })}
          icon={RefreshCw}
          testId="optimization-summary-cache"
        />
        <OptimizationMetric
          label={t('optimization.metric.cache_ratio')}
          value={formatRatio(cacheSummary.cacheReadWriteRatio)}
          detail={t('optimization.metric.read_write_detail', {
            read: formatTokens(cacheSummary.cacheReadTokens),
            write: formatTokens(cacheSummary.cacheWriteTokens),
          })}
          icon={ArrowLeftRight}
          testId="optimization-summary-cache-ratio"
        />
        <OptimizationMetric
          label={t('optimization.metric.input_volume')}
          value={summary !== undefined ? formatCompact(summary.totalInputTokens) : '-'}
          detail={
            summary !== undefined
              ? t('optimization.metric.output_tokens', {
                  tokens: formatCompact(summary.totalOutputTokens),
                })
              : t('optimization.metric.loading')
          }
          icon={Braces}
          testId="optimization-summary-volume"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]">
        <RecommendationsPanel
          keys={byKeyQ.data?.keys ?? []}
          summary={cacheSummary}
          bestCacheModel={bestCacheModel}
          isLoading={byKeyQ.isLoading || catalogQ.isLoading}
        />
        <ModelLab models={modelOptions} cnyPerUsd={cnyPerUsd} isLoading={catalogQ.isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <CostSimulator
          model={bestCacheModel}
          cnyPerUsd={cnyPerUsd}
          defaultInputTokens={summary?.totalInputTokens ?? 100_000}
        />
        <ContextTemplateBuilder />
      </div>

      <RequestHotspots
        rows={requestHotspots}
        isLoading={requestsQ.isLoading}
        cnyPerUsd={cnyPerUsd}
      />
    </section>
  );
}

function OptimizationMetric(props: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
  readonly icon: LucideIcon;
  readonly testId: string;
}): JSX.Element {
  const IconGlyph = props.icon;
  return (
    <article
      data-testid={props.testId}
      className="flex min-h-32 flex-col justify-between rounded-lg border border-line bg-card p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-mono-label uppercase text-ink-mid">{props.label}</span>
        <IconGlyph aria-hidden="true" className="h-5 w-5 text-ink-low" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-stat text-forest">{props.value}</span>
        <span className="text-caption text-ink-low">{props.detail}</span>
      </div>
    </article>
  );
}

function RecommendationsPanel(props: {
  readonly keys: readonly UsageByKeyEntry[];
  readonly summary: CacheSummary;
  readonly bestCacheModel: ModelOption | null;
  readonly isLoading: boolean;
}): JSX.Element {
  const t = useT();
  const recommendations = buildRecommendations(props.keys, props.summary, props.bestCacheModel, t);
  return (
    <article
      data-testid="optimization-recommendations"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('optimization.recommendations.title')}
        subtitle={t('optimization.recommendations.subtitle')}
      />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : (
        <div className="flex flex-col gap-3">
          {recommendations.map((item) => (
            <section
              key={item.title}
              className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-lg border border-line bg-raised p-4"
            >
              <span aria-hidden="true" className="mt-1 h-2 w-2 rounded-sm bg-rust" />
              <div className="min-w-0">
                <h3 className="text-body font-medium text-ink">{item.title}</h3>
                <p className="mt-1 text-body leading-6 text-ink-low">{item.body}</p>
              </div>
            </section>
          ))}
        </div>
      )}
    </article>
  );
}

function ModelLab(props: {
  readonly models: readonly ModelOption[];
  readonly cnyPerUsd: number | null;
  readonly isLoading: boolean;
}): JSX.Element {
  const t = useT();
  const cacheModels = props.models.filter((model) => model.model.supports_prompt_cache).slice(0, 4);
  return (
    <article
      data-testid="optimization-model-lab"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('optimization.model_lab.title')}
        subtitle={t('optimization.model_lab.subtitle')}
      />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : cacheModels.length === 0 ? (
        <p className="text-body text-ink-low">{t('optimization.model_lab.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {cacheModels.map((option) => (
            <div
              key={option.key}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-line pb-3 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{option.displayName}</p>
                <p className="font-mono text-caption text-ink-low">
                  {t('optimization.model_lab.meta', {
                    window: formatContextWindow(option.model.context_window, t),
                    rate: formatRate(
                      option.cacheReadRateUsd,
                      option.effectiveMultiplier,
                      props.cnyPerUsd,
                    ),
                  })}
                </p>
              </div>
              <div className="text-right font-mono text-caption text-ink-low">
                <p className="font-medium text-forest">
                  {formatRate(option.inputRateUsd, option.effectiveMultiplier, props.cnyPerUsd)}
                </p>
                <p>{t('optimization.model_lab.input_unit')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function CostSimulator(props: {
  readonly model: ModelOption | null;
  readonly cnyPerUsd: number | null;
  readonly defaultInputTokens: number;
}): JSX.Element {
  const t = useT();
  const [inputTokens, setInputTokens] = useState(props.defaultInputTokens);
  const [cachedPercent, setCachedPercent] = useState(60);
  const model = props.model;
  const naiveUsd =
    model === null
      ? 0
      : (Math.max(0, inputTokens) / 1_000_000) * model.inputRateUsd * model.effectiveMultiplier;
  const cachedUsd =
    model === null
      ? 0
      : (Math.max(0, inputTokens) / 1_000_000) *
        (((100 - cachedPercent) / 100) * model.inputRateUsd +
          (cachedPercent / 100) * (model.cacheReadRateUsd ?? model.inputRateUsd)) *
        model.effectiveMultiplier;
  const savingsPct = naiveUsd > 0 ? Math.max(0, 1 - cachedUsd / naiveUsd) : null;

  return (
    <article
      data-testid="optimization-cost-simulator"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('optimization.simulator.title')}
        subtitle={t('optimization.simulator.subtitle')}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('optimization.simulator.input_tokens')}
          </span>
          <input
            data-testid="optimization-sim-input"
            type="number"
            min={0}
            value={inputTokens}
            onChange={(event) => setInputTokens(readNumericInput(event.currentTarget.value))}
            className="rounded-lg border border-line bg-raised px-3 py-2 font-mono text-body text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('optimization.simulator.cacheable_prefix')}
          </span>
          <input
            type="range"
            min={0}
            max={95}
            value={cachedPercent}
            onChange={(event) => setCachedPercent(readNumericInput(event.currentTarget.value))}
            className="mt-3 accent-forest"
          />
          <span className="font-mono text-caption text-ink-low">
            {t('optimization.simulator.cached_percent', { percent: cachedPercent })}
          </span>
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SimulatorMetric
          label={t('optimization.simulator.naive')}
          value={formatUsdAsCny(naiveUsd, props.cnyPerUsd)}
          testId="optimization-sim-naive"
        />
        <SimulatorMetric
          label={t('optimization.simulator.cached')}
          value={formatUsdAsCny(cachedUsd, props.cnyPerUsd)}
          testId="optimization-sim-cached"
        />
        <SimulatorMetric
          label={t('optimization.simulator.savings')}
          value={formatPercent(savingsPct)}
          testId="optimization-sim-savings"
        />
      </div>
      <p className="text-caption leading-5 text-ink-low">
        {model === null
          ? t('optimization.simulator.no_catalog')
          : t('optimization.simulator.using_model', { model: model.displayName })}
      </p>
    </article>
  );
}

function ContextTemplateBuilder(): JSX.Element {
  const t = useT();
  const [enabled, setEnabled] = useState<ReadonlySet<string>>(() => new Set(['node']));
  const template = buildContextTemplate(enabled, (group) => templateGroupLabel(group, t));
  return (
    <article
      data-testid="optimization-template-builder"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('optimization.template.title')}
        subtitle={t('optimization.template.subtitle')}
      />
      <div className="flex flex-wrap gap-2">
        {TEMPLATE_GROUPS.map((group) => {
          const checked = enabled.has(group.id);
          return (
            <button
              key={group.id}
              type="button"
              data-testid={`optimization-template-${group.id}`}
              aria-pressed={checked}
              onClick={() => {
                setEnabled((current) => {
                  const next = new Set(current);
                  if (next.has(group.id)) next.delete(group.id);
                  else next.add(group.id);
                  return next;
                });
              }}
              className={[
                'rounded-lg border px-3 py-2 text-caption font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
                checked
                  ? 'border-forest bg-forest text-on-forest'
                  : 'border-line bg-raised text-ink-low hover:text-ink',
              ].join(' ')}
            >
              {templateGroupLabel(group, t)}
            </button>
          );
        })}
      </div>
      <pre
        data-testid="optimization-context-template"
        className="max-h-72 overflow-auto rounded-lg border border-line bg-raised p-4 font-mono text-caption leading-6 text-ink"
      >
        {template}
      </pre>
    </article>
  );
}

function RequestHotspots(props: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly isLoading: boolean;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="optimization-request-hotspots"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title={t('optimization.hotspots.title')}
        subtitle={t('optimization.hotspots.subtitle')}
      />
      {props.isLoading ? (
        <Skeleton className="h-40" />
      ) : props.rows.length === 0 ? (
        <p className="text-body text-ink-low">{t('optimization.hotspots.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-body">
            <thead>
              <tr className="text-left text-[10px] uppercase text-ink-low">
                <th className="px-3 py-2 font-medium">
                  {t('optimization.hotspots.column.request')}
                </th>
                <th className="px-3 py-2 font-medium">{t('requests.column.model')}</th>
                <th className="px-3 py-2 font-medium">
                  {t('optimization.hotspots.column.tokens')}
                </th>
                <th className="px-3 py-2 font-medium">{t('optimization.hotspots.column.cache')}</th>
                <th className="px-3 py-2 font-medium">{t('usage.summary.spend')}</th>
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row) => (
                <tr key={row.id} className="odd:bg-raised">
                  <td className="px-3 py-2 font-mono text-ink">{row.requestId}</td>
                  <td className="px-3 py-2 font-mono text-ink-mid">{row.model}</td>
                  {/* #1670: unit-metered rows dash instead of showing fake 0s. */}
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {unitAwareTokenCell(row, formatCompact(row.inputTokens + row.outputTokens))}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {unitAwareTokenCell(
                      row,
                      t('optimization.hotspots.cache_cell', {
                        read: formatTokens(row.cacheReadTokens),
                        write: formatTokens(row.cacheWriteTokens),
                      }),
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-forest">
                    {isRatingPending(row) ? (
                      <span className="text-ink-low">{t('requests.cost.pending')}</span>
                    ) : (
                      formatUsdMicrosOrCentsAsCnyMetered(
                        row.costUsdMicros,
                        row.costUsdCents,
                        props.cnyPerUsd,
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

/**
 * The same #1410 P3 rule `Requests.tsx` applies to its cost cell: under async
 * rating a request is visible before its price is known, so formatting the
 * placeholder zero would tell a paying customer the request was free minutes
 * before the charge lands. Two consumers of one wire rule — keep them in step.
 *
 * `ratingStatus` is optional on the wire (a gateway predating P3 omits it) and
 * every such row is final, hence `'rated'` as the fallback rather than
 * `'pending'`, which would paint the whole window as unsettled. `'not_billable'`
 * is likewise settled — at zero — so it keeps rendering the amount.
 *
 * The hotspot table sorts by spend and keeps the top rows, so an unrated row
 * (whose spend still reads as zero) surfaces here whenever the window holds
 * fewer requests than the cut-off.
 */
function isRatingPending(row: UsageRequestLogEntry): boolean {
  return (row.ratingStatus ?? 'rated') === 'pending';
}

function PanelHeader(props: { readonly title: string; readonly subtitle: string }): JSX.Element {
  return (
    <header className="flex flex-col gap-1">
      <h3 className="font-mono text-mono-label uppercase text-ink-mid">{props.title}</h3>
      <p className="text-caption leading-5 text-ink-low">{props.subtitle}</p>
    </header>
  );
}

function SimulatorMetric(props: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div data-testid={props.testId} className="rounded-lg border border-line bg-raised p-3">
      <p className="font-mono text-mono-label uppercase text-ink-low">{props.label}</p>
      <p className="mt-2 font-mono text-lg font-medium text-forest">{props.value}</p>
    </div>
  );
}

function summarizeCache(keys: readonly UsageByKeyEntry[]): CacheSummary {
  const totals = keys.reduce(
    (acc, key) => ({
      successRequests: acc.successRequests + key.successRequests,
      cacheHitRequests: acc.cacheHitRequests + key.cacheHitRequests,
      cacheEligibleInputTokens: acc.cacheEligibleInputTokens + key.cacheEligibleInputTokens,
      cacheReadTokens: acc.cacheReadTokens + key.cacheReadTokens,
      cacheWriteTokens: acc.cacheWriteTokens + key.cacheWriteTokens,
    }),
    {
      successRequests: 0,
      cacheHitRequests: 0,
      cacheEligibleInputTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
    },
  );
  return {
    ...totals,
    requestCacheHitRate: nullableRatio(totals.cacheHitRequests, totals.successRequests),
    tokenCacheHitRate: nullableRatio(totals.cacheReadTokens, totals.cacheEligibleInputTokens),
    cacheReadWriteRatio: nullableRatio(totals.cacheReadTokens, totals.cacheWriteTokens),
  };
}

function buildModelOptions(catalog: CatalogResponse | undefined): readonly ModelOption[] {
  if (catalog === undefined) return [];
  return catalog.models
    .flatMap((model) => {
      // Unit-metered models (#1706) price by the unit, not per Mtok: their
      // token dimensions are the zero-rate anchor generation, so admitting
      // them here would rank them as free and put them first in every
      // savings estimate. They are not on this axis at all — exclude.
      if (model.unit_pricing !== undefined) return [];
      if (model.input_per_mtok_usd === null || model.output_per_mtok_usd === null) return [];
      const bestChannel = model.channels
        .slice()
        .sort(
          (a, b) =>
            a.rate_multiplier - b.rate_multiplier || a.display_name.localeCompare(b.display_name),
        )[0];
      if (bestChannel === undefined) return [];
      return [
        {
          key: `${model.vendor}:${model.model}`,
          model,
          displayName: model.display_name ?? model.model,
          inputRateUsd: model.input_per_mtok_usd,
          outputRateUsd: model.output_per_mtok_usd,
          cacheReadRateUsd: model.cache_read_per_mtok_usd,
          cacheWriteRateUsd:
            model.cache_write_5m_per_mtok_usd ?? model.cache_write_per_mtok_usd ?? null,
          effectiveMultiplier: bestChannel.rate_multiplier,
        },
      ];
    })
    .sort((a, b) => {
      const cacheDelta =
        Number(b.model.supports_prompt_cache) - Number(a.model.supports_prompt_cache);
      if (cacheDelta !== 0) return cacheDelta;
      return a.inputRateUsd * a.effectiveMultiplier - b.inputRateUsd * b.effectiveMultiplier;
    });
}

function buildRecommendations(
  keys: readonly UsageByKeyEntry[],
  summary: CacheSummary,
  bestCacheModel: ModelOption | null,
  t: Translate,
): readonly { readonly title: string; readonly body: string }[] {
  const worstKey = keys.slice().sort(compareKeysByCacheWaste)[0] ?? null;
  const recommendations = [
    {
      title: t('optimization.recommendations.prefix.title'),
      body:
        worstKey === null
          ? t('optimization.recommendations.prefix.body_empty')
          : t('optimization.recommendations.prefix.body', {
              key: displayKey(worstKey, t),
              hit: formatPercent(worstKey.tokenCacheHitRate),
              ratio: formatRatio(worstKey.cacheReadWriteRatio),
            }),
    },
    {
      title: t('optimization.recommendations.split.title'),
      body: t('optimization.recommendations.split.body', {
        tokens: formatCompact(summary.cacheWriteTokens),
      }),
    },
  ];
  if (bestCacheModel !== null) {
    recommendations.push({
      title: t('optimization.recommendations.model.title'),
      body: t('optimization.recommendations.model.body', { model: bestCacheModel.displayName }),
    });
  }
  return recommendations;
}

function buildContextTemplate(
  enabled: ReadonlySet<string>,
  labelFor: (group: (typeof TEMPLATE_GROUPS)[number]) => string,
): string {
  const lines = [
    '# Context hygiene',
    '.git/',
    '.DS_Store',
    '*.log',
    '*.tmp',
    '.env',
    '.env.*',
    'coverage/',
    'tmp/',
  ];
  for (const group of TEMPLATE_GROUPS) {
    if (enabled.has(group.id)) {
      lines.push('', `# ${labelFor(group)}`, ...group.lines);
    }
  }
  return lines.join('\n');
}

function compareKeysByCacheWaste(a: UsageByKeyEntry, b: UsageByKeyEntry): number {
  const aWaste = a.cacheWriteTokens - a.cacheReadTokens;
  const bWaste = b.cacheWriteTokens - b.cacheReadTokens;
  return bWaste - aWaste || (a.tokenCacheHitRate ?? 1) - (b.tokenCacheHitRate ?? 1);
}

function compareRequestsBySpend(a: UsageRequestLogEntry, b: UsageRequestLogEntry): number {
  const costDelta = usdMicrosFromUsage(b) - usdMicrosFromUsage(a);
  if (costDelta !== 0) return costDelta;
  return b.inputTokens + b.outputTokens - (a.inputTokens + a.outputTokens);
}

function usdMicrosFromUsage(row: UsageRequestLogEntry): number {
  return row.costUsdMicros ?? row.costUsdCents * 10_000;
}

function displayKey(key: UsageByKeyEntry, t: Translate): string {
  return apiKeyDisplayName(key.apiKeyName, key.apiKeyPrefix, t);
}

function nullableRatio(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return numerator / denominator;
}

function readNumericInput(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
}

function formatPercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return `${Math.round(value * 100)}%`;
}

function formatRatio(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return `${value.toFixed(value >= 10 ? 0 : 1)}x`;
}

function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return '-';
  if (Math.abs(value) >= 1_000_000)
    return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return formatTokens(value);
}

function formatContextWindow(value: number | null, t: Translate): string {
  if (value === null) return t('optimization.model_lab.context_unknown');
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return formatTokens(value);
}

function formatRate(
  usdRate: number | null | undefined,
  multiplier: number,
  cnyPerUsd: number | null,
): string {
  if (typeof usdRate !== 'number' || !Number.isFinite(usdRate) || cnyPerUsd === null) return '-';
  return formatCnyRateFromUsdRate(usdRate * multiplier, cnyPerUsd);
}

function formatUsdAsCny(usd: number, cnyPerUsd: number | null): string {
  if (!Number.isFinite(usd) || cnyPerUsd === null) return '-';
  return formatUsdMicrosAsCnyDisplay(Math.round(usd * 1_000_000), cnyPerUsd);
}
